import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { ErrorBoundary, Component as Root } from '~/routes/layouts/root';
import SignIn from '~/routes/signin';
import Silent from '~/routes/silent';
import { Component as TrackingPage } from '~/routes/tracking/result';
import { Component as TrackingSearchPage } from '~/routes/tracking/search';
import './app.css';

Sentry.init({
  environment: import.meta.env.VITE_ENVIRONMENT || 'Debug',
  release: import.meta.env.VITE_VERSION || '0.0.0',
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailAllowUrls: [window.location.origin],
      networkResponseHeaders: [
        'x-tenant',
        'x-region',
        'x-trace',
        'x-cache',
        'cf-cache-status',
        'x-amzn-trace-id',
        'cache-control'
      ],
      unblock: ['.sentry-unblock, [data-sentry-unblock]'],
      unmask: ['.sentry-unmask, [data-sentry-unmask]']
    }),
    Sentry.thirdPartyErrorFilterIntegration({
      filterKeys: ['parcel2go-uiz'],
      behaviour: 'drop-error-if-contains-third-party-frames'
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 0.01, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.01, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    path: ':prefix?',
    element: <Root />,
    children: [
      {
        path: ':prefix?/tracking',
        element: <TrackingSearchPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: ':prefix?/tracking/:order',
        element: <TrackingPage />,
        errorElement: <ErrorBoundary />
      }
    ]
  },
  {
    path: '/ui/statics/signin-callback',
    element: <SignIn />
  },
  {
    path: '/ui/statics/silent-callback',
    element: <Silent />
  }
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
